import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { get } from '../api/client.js';
import { BELL_ACK_CODE_LABELS } from '../lib/format.js';
import { Notice } from './ui.js';

export interface TrackedCommand {
  deviceId: string;
  commandId: string;
  deviceName: string;
  /** What was asked, as a noun phrase: «تشغيل «رسالة الصباح»», «بدء اليوم الدراسي». */
  action: string;
  expiresAt: string | null;
  /** Something the platform cannot verify, e.g. a file on a v2 MP3-module card. */
  warning?: string | null;
}

interface CommandRow {
  command_id: string;
  ack_stage: string | null;
  ack_code: string | null;
}

const FINAL = new Set(['started', 'completed', 'rejected', 'failed', 'expired']);

/**
 * Follows one device command in plain words — sent, received, running or
 * refused and why — instead of handing the operator a command id to go and
 * look up.
 */
export function CommandProgress({ command }: { command: TrackedCommand }): JSX.Element {
  const [, setTick] = useState(0);

  const commands = useQuery({
    queryKey: ['command-progress', command.commandId],
    queryFn: () => get<{ items: CommandRow[] }>(`/devices/${command.deviceId}/commands?page_size=25`),
    refetchInterval: (query) => {
      const row = (query.state.data as { items: CommandRow[] } | undefined)?.items.find(
        (r) => r.command_id === command.commandId,
      );
      if (row?.ack_stage && FINAL.has(row.ack_stage)) return false;
      if (command.expiresAt) {
        const expires = Date.parse(command.expiresAt);
        // Never picked up: it never will be. Picked up: a download (play once) can take minutes.
        if (!row?.ack_stage && Date.now() > expires + 5_000) return false;
        if (Date.now() > expires + 10 * 60_000) return false;
      }
      return 1_500;
    },
  });

  const row = commands.data?.items.find((r) => r.command_id === command.commandId);
  const stage = row?.ack_stage ?? null;
  const timedOut = !stage && command.expiresAt !== null && Date.now() > Date.parse(command.expiresAt);
  const done = (stage !== null && FINAL.has(stage)) || timedOut;

  // Re-render while waiting so a silent timeout still shows.
  useEffect(() => {
    if (done) return;
    const t = setInterval(() => setTick((n) => n + 1), 1_000);
    return () => clearInterval(t);
  }, [done]);

  const device = `«${command.deviceName}»`;
  const reason = row?.ack_code ? (BELL_ACK_CODE_LABELS[row.ack_code] ?? row.ack_code) : 'سبب غير معروف';

  let tone: 'info' | 'warn' | 'danger' = 'info';
  let text: string;
  if (stage === 'rejected' || stage === 'failed') {
    tone = 'danger';
    text = `✖ لم ينفّذ الجهاز ${device} «${command.action}»: ${reason}.`;
  } else if (stage === 'expired' || timedOut) {
    tone = 'warn';
    text = `لم يستلم الجهاز ${device} الطلب في الوقت المحدد، فأُلغي ولن يُنفَّذ لاحقًا. تأكد أن الجهاز متصل بالإنترنت ثم أعد المحاولة.`;
  } else if (stage === 'completed') {
    text = `✔ نفّذ الجهاز ${device}: ${command.action}.`;
  } else if (stage === 'started') {
    text = `✔ الجهاز ${device} ينفّذ الآن: ${command.action}.`;
  } else if (stage === 'received' || stage === 'accepted') {
    text =
      row?.ack_code === 'downloading'
        ? `⏳ الجهاز ${device} ينزّل المقطع الآن، ويبدأ بثه فور اكتمال التنزيل…`
        : `استلم الجهاز ${device} الطلب، جارٍ التنفيذ…`;
  } else {
    text = `⏳ جارٍ إرسال «${command.action}» إلى الجهاز ${device}… يستلمه عادة خلال ثانيتين.`;
  }

  const showWarning = command.warning && stage !== 'rejected' && stage !== 'failed' && !timedOut && stage !== 'expired';

  return (
    <>
      <Notice tone={tone}>{text}</Notice>
      {showWarning ? <Notice tone="warn">{command.warning}</Notice> : null}
    </>
  );
}
